import React, { useState, useMemo } from 'react'
import { View, ScrollView, TouchableOpacity } from 'react-native'
import { useTheme } from '@react-navigation/native'
import {
  Header,
  Screen,
  TextInput,
  Button,
  Text,
  Icon,
  KeyboardAwareView,
  BackButtonWeb,
} from '@components'
import { FormHashtagList } from '@components'
import { useForm } from 'react-hook-form'
import {
  ContactListItem,
  GenderType,
  getCommonStyles,
  getDayLabel,
  getFormatedList,
  getMomentLabel,
  groupItemsBySector,
  SelectionItem,
} from '@utils'
import { GenericSelectionModal } from '../../../components/organisms/GenericSelectionModal'
import { AvailabilityModal } from '../../../components/organisms'
import { FormDate } from '../../../components/organisms'
import { GroupedItem } from '@utils'
import {
  useGetProfile,
  cityToSelectionItem,
  usePaginateCities,
  usePaginateCountries,
  usePaginateLanguages,
  useScreen,
  useGetProfessionSectors,
  usePaginateProfessions,
  usePaginateCategories,
  usePaginateHashtags,
} from '@hooks'
import { getAddGhostContactStyles } from '../../../screens/contacts/addGhostContact/styles'
import { useAddGhostContact } from '../../../screens/contacts/addGhostContact/hooks'
import { TabSection } from '../../../screens/contacts/addGhostContact/components/TabSection'
import { SectionTitle } from '../../../screens/contacts/addGhostContact/components'
import { useLanguage } from '@context'

export const Edit: React.FC = () => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()

  const { isMobile, isWeb, isDesktop } = useScreen()
  const styles = getAddGhostContactStyles(theme, isWeb)
  const commonStyles = getCommonStyles(theme)

  const { control } = useForm<any>()

  const [searchText, setSearchText] = useState('')
  const {
    flatData: categories,
    isFetching: isFetchingCategories,
    isFetchingNextPage: isFetchingNextPageCategories,
    fetchNextPage: fetchNextPageCategories,
    hasNextPage: hasNextPageCategories,
  } = usePaginateCategories(searchText)
  const {
    flatData: hashtags,
    isFetching: isFetchingHashtags,
    isFetchingNextPage: isFetchingNextPageHashtags,
    fetchNextPage: fetchNextPageHashtags,
    hasNextPage: hasNextPageHashtags,
  } = usePaginateHashtags(searchText)
  const {
    flatData: countries,
    isFetching: isFetchingCountries,
    isFetchingNextPage: isFetchingNextPageCountries,
    fetchNextPage: fetchNextPageCountries,
    hasNextPage: hasNextPageCountries,
  } = usePaginateCountries({ search: searchText, lang: currentLanguage })
  const {
    flatData: allLanguages,
    isFetching: isFetchingLanguages,
    isFetchingNextPage: isFetchingNextPageLanguages,
    fetchNextPage: fetchNextPageLanguages,
    hasNextPage: hasNextPageLanguages,
  } = usePaginateLanguages({ search: searchText, lang: currentLanguage })

  // Fetch profession sectors and professions from API
  const { data: professionSectors = [] } = useGetProfessionSectors()
  const {
    flatData: professions,
    isFetching: isFetchingProfessions,
    isFetchingNextPage: isFetchingNextPageProfessions,
    fetchNextPage: fetchNextPageProfessions,
    hasNextPage: hasNextPageProfessions,
  } = usePaginateProfessions(searchText)

  // Group professions by sector
  const groupedProfessions: GroupedItem[] = useMemo(() => {
    return groupItemsBySector(professionSectors, professions)
  }, [professionSectors, professions])

  const { data: profile } = useGetProfile()

  const [isLanguagesModalVisible, setIsLanguagesModalVisible] = useState(false)
  const [isDisponibilityModalVisible, setIsDisponibilityModalVisible] = useState(false)
  const [isCategoriesModalVisible, setIsCategoriesModalVisible] = useState(false)
  const [isHashtagsModalVisible, setIsHashtagsModalVisible] = useState(false)
  const [isNationalitiesModalVisible, setIsNationalitiesModalVisible] = useState(false)
  const [isOriginModalVisible, setIsOriginModalVisible] = useState(false)
  const [isLivedPlacesModalVisible, setIsLivedPlacesModalVisible] = useState(false)
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false)
  const [isProfessionsModalVisible, setIsProfessionsModalVisible] = useState(false)
  // The search box is shared by every picker of this form: the cities are
  // searched only while one of their two pickers is open.
  const {
    flatData: cities,
    isFetching: isFetchingCities,
    isFetchingNextPage: isFetchingNextPageCities,
    fetchNextPage: fetchNextPageCities,
    hasNextPage: hasNextPageCities,
  } = usePaginateCities({
    search: isAddressModalVisible || isLivedPlacesModalVisible ? searchText : '',
    lang: currentLanguage,
  })

  const { formData, handleSave, handleFormChange, isSaving } = useAddGhostContact(profile)

  const handleGenderSelect = (gender: GenderType) => {
    handleFormChange('gender', gender)
  }

  const handleAddHashtag = (key: string, value: string[]) => {
    handleFormChange(key as keyof ContactListItem, value)
  }

  const handleRemoveHashtag = (key: string, value: string[]) =>
    handleFormChange(key as keyof ContactListItem, value)

  return (
    <Screen>
      <BackButtonWeb title={t('profileEdit.backToMyProfile')} />
      <Header
        title={t('profileEdit.editMyProfile')}
        /*iconLeft="arrowBack"
        iconColor={theme.colors.secondary}
        onPressLeft={() => {
          if (isModalContext && props?.onBack) {
            props.onBack()
          } else {
            navigation?.goBack()
          }
        }}*/
      />

      <KeyboardAwareView isFullScreen>
        <ScrollView style={styles.container}>
          <View style={styles.form}>
            <TabSection
              firstTab={
                <>
                  <View style={[styles.genderContainer, !isMobile && { alignSelf: 'flex-start' }]}>
                    <TouchableOpacity
                      style={[
                        styles.genderButton,
                        formData.gender === 'female' && styles.genderButtonSelected,
                      ]}
                      onPress={() => handleGenderSelect('female')}
                    >
                      <Text
                        type="bodySmall"
                        color={
                          formData.gender === 'female' ? theme.colors.white : theme.colors.text
                        }
                      >
                        {t('form.woman')}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        styles.genderButton,
                        formData.gender === 'male' && styles.genderButtonSelected,
                      ]}
                      onPress={() => handleGenderSelect('male')}
                    >
                      <Text
                        type="bodySmall"
                        color={formData.gender === 'male' ? theme.colors.white : theme.colors.text}
                      >
                        {t('form.man')}
                      </Text>
                    </TouchableOpacity>

                    {/*<TouchableOpacity
                      style={[
                        styles.genderButton,
                        formData.gender === 'non-binary' && styles.genderButtonSelected
                      ]}
                      onPress={() => handleGenderSelect('non-binary')}
                    >
                      <Text
                        type="body"
                        color={formData.gender === 'non-binary' ? theme.colors.white : theme.colors.text}
                      >
                        Non binaire
                      </Text>
                    </TouchableOpacity>*/}
                  </View>
                  <View
                    style={
                      isMobile
                        ? commonStyles.flex
                        : [commonStyles.rowGapLg, { alignItems: 'flex-start' }]
                    }
                  >
                    <View style={isDesktop && [styles.fixedWidth, styles.marginRight]}>
                      {/* Section Identité */}
                      <SectionTitle title={t('form.mainInfo')}>
                        <TextInput
                          title={t('form.firstName')}
                          value={formData.firstName}
                          onChangeText={(text) => handleFormChange('firstName', text)}
                          placeholder={t('form.enterFirstName')}
                          containerStyle={styles.input}
                          iconRight="userPlus"
                        />

                        <TextInput
                          title={t('form.lastName')}
                          value={formData.lastName}
                          onChangeText={(text) => handleFormChange('lastName', text)}
                          placeholder={t('form.enterLastName')}
                          containerStyle={styles.input}
                          iconRight="userPlus"
                        />

                        <TouchableOpacity onPress={() => setIsProfessionsModalVisible(true)}>
                          <TextInput
                            title={t('form.professions')}
                            value={getFormatedList(
                              formData.professions?.map((profession) => profession.name) || [],
                            )}
                            placeholder={t('form.enterProfession')}
                            containerStyle={styles.input}
                            iconRight="work"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                        <TextInput
                          title={t('form.email')}
                          value={formData.email}
                          onChangeText={(text) => handleFormChange('email', text)}
                          placeholder={t('form.email')}
                          containerStyle={styles.input}
                          keyboardType="email-address"
                          iconRight="message"
                        />

                        <TextInput
                          title={t('form.phoneNumber')}
                          value={formData.phone}
                          onChangeText={(text) => handleFormChange('phone', text)}
                          placeholder={t('form.phoneNumber')}
                          containerStyle={styles.input}
                          keyboardType="phone-pad"
                          iconRight="phone"
                        />
                        {/*<TextInput
                          title={"Notes :"}
                          containerStyle={styles.input}
                          value={formData.notes}
                          onChangeText={(text) => handleFormChange('notes', text)}
                          placeholder="Notes"
                          multiline
                          numberOfLines={4}
                          iconRight="note"
                        />*/}
                      </SectionTitle>
                    </View>
                    <View style={isDesktop && styles.fixedWidth}>
                      {/* Section Contacts */}
                      <SectionTitle title={t('form.socialNetworks')}>
                        <TextInput
                          value={formData.facebook}
                          onChangeText={(text) => handleFormChange('facebook', text)}
                          placeholder={t('ghostForm.facebook')}
                          containerStyle={styles.input}
                          iconRight="facebook"
                          iconColor={theme.colors.primary}
                        />

                        <TextInput
                          value={formData.instagram}
                          onChangeText={(text) => handleFormChange('instagram', text)}
                          placeholder={t('ghostForm.instagram')}
                          containerStyle={styles.input}
                          iconRight="instagram"
                          iconColor={theme.colors.primary}
                        />

                        <TextInput
                          value={formData.linkedin}
                          onChangeText={(text) => handleFormChange('linkedin', text)}
                          placeholder={t('ghostForm.linkedin')}
                          containerStyle={styles.input}
                          iconRight="linkedin"
                          iconColor={theme.colors.primary}
                        />

                        <TextInput
                          value={formData.twitter}
                          onChangeText={(text) => handleFormChange('twitter', text)}
                          placeholder={t('ghostForm.twitter')}
                          containerStyle={styles.input}
                          iconRight="twitter"
                          iconColor={theme.colors.primary}
                        />
                        <TextInput
                          title={t('form.website')}
                          value={formData.website}
                          onChangeText={(text) => handleFormChange('website', text)}
                          placeholder={t('form.website')}
                          containerStyle={styles.input}
                          keyboardType="url"
                          iconRight="globe"
                        />
                      </SectionTitle>
                    </View>
                  </View>
                </>
              }
              secondTab={
                <>
                  <View
                    style={
                      isMobile
                        ? commonStyles.flex
                        : [commonStyles.rowGapLg, { alignItems: 'flex-start' }]
                    }
                  >
                    <View style={isDesktop && [styles.fixedWidth, styles.marginRight]}>
                      <SectionTitle title={t('form.presentation')}>
                        <TextInput
                          title={t('profileEdit.about')}
                          iconRight="edit"
                          containerStyle={styles.input}
                          value={formData.about}
                          onChangeText={(text) => handleFormChange('about', text)}
                          placeholder={t('form.descriptionPlaceholder')}
                          isMultiline
                          scrollEnabled={false}
                        />
                        <FormDate
                          control={control}
                          name="birthDate"
                          title={t('form.birthDate')}
                          icon="calendar"
                          placeholder="JJ-MM-AAAA"
                          value={formData.birthDate}
                          onChange={(date) => handleFormChange('birthDate', date)}
                        />
                        <TouchableOpacity
                          onPress={() => setIsAddressModalVisible(true)}
                          style={commonStyles.paddingTop}
                        >
                          <TextInput
                            title={t('form.livingPlace')}
                            value={formData.residence?.name}
                            onChangeText={(text) => handleFormChange('residence', text)}
                            placeholder={t('form.livingPlace')}
                            containerStyle={styles.input}
                            iconRight="globe"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setIsOriginModalVisible(true)}>
                          <TextInput
                            title={t('form.origins')}
                            value={getFormatedList(
                              (formData.origins || []).map((origin) => origin.name),
                            )}
                            onChangeText={(text) => handleFormChange('origins', text)}
                            placeholder={t('form.origins')}
                            containerStyle={styles.input}
                            iconRight="globe"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setIsNationalitiesModalVisible(true)}>
                          <TextInput
                            title={t('form.nationalities')}
                            value={getFormatedList(
                              formData.nationalities?.map((nationality) => nationality.name) || [],
                            )}
                            onChangeText={(text) => handleFormChange('nationalities', text)}
                            placeholder={t('form.nationalities')}
                            containerStyle={styles.input}
                            iconRight="globe"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>

                        <TouchableOpacity onPress={() => setIsLanguagesModalVisible(true)}>
                          <TextInput
                            title={t('form.spokenLanguages')}
                            value={getFormatedList(
                              formData.languages?.map((language) => language.name) || [],
                            )}
                            onChangeText={(text) => handleFormChange('languages', text)}
                            placeholder={t('form.enterLanguages')}
                            containerStyle={styles.input}
                            iconRight="translate"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setIsLivedPlacesModalVisible(true)}>
                          <TextInput
                            title={t('form.livedPlaces')}
                            value={getFormatedList(
                              formData.residenceHistory?.map((place) => place.name) || [],
                            )}
                            onChangeText={(text) => handleFormChange('residenceHistory', text)}
                            placeholder={t('form.livedPlaces')}
                            containerStyle={styles.input}
                            iconRight="mapPin"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                      </SectionTitle>
                    </View>
                    <View style={isDesktop && styles.fixedWidth}>
                      <SectionTitle title={t('form.availability')}>
                        <View style={styles.disponibilityBackground}>
                          <Icon
                            name="edit"
                            color={theme.colors.greyBlue}
                            size={theme.iconSize.medium}
                            containerStyle={styles.editIcon}
                          />
                          <TouchableOpacity onPress={() => setIsDisponibilityModalVisible(true)}>
                            <Text
                              type="bodySmall"
                              color={theme.colors.lightGrey}
                              style={styles.title}
                            >
                              {t('form.availability')}
                            </Text>
                            {formData.disponibilities && formData.disponibilities.length > 0 && (
                              <View style={styles.disponibilityContainer}>
                                <View style={{ marginTop: theme.spacing.sm }}>
                                  {formData.disponibilities.map((d, index) => (
                                    <View key={index} style={styles.disponibilityItem}>
                                      <Text type="body">
                                        {getDayLabel(t, d.dayStart?.id) || d.dayStart?.name || ''} -{' '}
                                        {getDayLabel(t, d.dayEnd?.id) || d.dayEnd?.name || ''}{' '}
                                        {d.timeStart} - {d.timeEnd}
                                      </Text>
                                    </View>
                                  ))}
                                </View>
                              </View>
                            )}
                          </TouchableOpacity>
                          {formData.bestMoments && formData.bestMoments.length > 0 ? (
                            <View style={styles.bestMomentsSection}>
                              <Text
                                type="bodySmall"
                                color={theme.colors.lightGrey}
                                style={styles.bestMomentsTitle}
                              >
                                {t('form.bestMoment')}
                              </Text>

                              <ScrollView
                                horizontal
                                showsHorizontalScrollIndicator={false}
                                contentContainerStyle={styles.bestMomentsScrollContent}
                                style={styles.bestMomentsScroll}
                              >
                                {formData.bestMoments?.map((moment, index) => (
                                  <View key={index} style={styles.bestMomentItem}>
                                    <Icon
                                      name={moment.code}
                                      color={theme.colors.primary}
                                      size={theme.iconSize.large}
                                    />
                                    <Text
                                      type="bodySmall"
                                      color={theme.colors.primary}
                                      style={styles.bestMomentLabel}
                                    >
                                      {getMomentLabel(
                                        t,
                                        moment.code,
                                        moment.nameFr,
                                        moment.nameEn,
                                        currentLanguage,
                                        moment.nameEs,
                                      )}
                                    </Text>
                                  </View>
                                ))}
                              </ScrollView>
                            </View>
                          ) : (
                            <></>
                          )}
                        </View>
                      </SectionTitle>
                      {/* Section Tags */}
                      <SectionTitle title={t('ghostForm.tagsSection')} collapsible>
                        <FormHashtagList
                          control={control}
                          name="Hashtags"
                          placeholder={t('ghostForm.hashtagPlaceholder')}
                          values={formData.hashtags || []}
                          isGhost
                          iconRight="htag"
                          addItem={(value) => handleAddHashtag('hashtags', value)}
                          removeItem={(value) => handleRemoveHashtag('hashtags', value)}
                        />
                        <FormHashtagList
                          control={control}
                          name="Objectifs"
                          placeholder={t('ghostForm.goalPlaceholder')}
                          values={formData.goals || []}
                          isGhost
                          isSentence
                          iconRight="translate"
                          addItem={(value) => handleAddHashtag('goals', value)}
                          removeItem={(value) => handleRemoveHashtag('goals', value)}
                        />
                        <FormHashtagList
                          control={control}
                          name="Hobbies"
                          placeholder="#faire_du_karate..."
                          values={formData.hobbies || []}
                          isGhost
                          isSingleTag
                          iconRight="heart"
                          addItem={(value) => handleAddHashtag('hobbies', value)}
                          removeItem={(value) => handleRemoveHashtag('hobbies', value)}
                        />
                        <FormHashtagList
                          control={control}
                          name="Compétences"
                          placeholder="#marketing..."
                          values={formData.skills || []}
                          isGhost
                          isSingleTag
                          iconRight="heart"
                          addItem={(value) => handleAddHashtag('skills', value)}
                          removeItem={(value) => handleRemoveHashtag('skills', value)}
                        />
                      </SectionTitle>
                    </View>
                  </View>
                </>
              }
            />
          </View>
        </ScrollView>
      </KeyboardAwareView>
      <View
        style={
          isMobile ? [commonStyles.paddingHorizontal, commonStyles.paddingBottom] : styles.footer
        }
      >
        <Button
          title={t('common.save')}
          onPress={() => handleSave()}
          unfilled={false}
          isSmall={!isMobile}
          // My own first and last name stay mandatory.
          disabled={isSaving || !formData.firstName?.trim() || !formData.lastName?.trim()}
        />
      </View>
      <GenericSelectionModal
        isVisible={isLanguagesModalVisible}
        setIsVisible={setIsLanguagesModalVisible}
        title={t('form.spokenLanguages')}
        placeholder={t('form.searchLanguage')}
        availableItems={allLanguages.map((language) => ({
          id: language.code,
          name: language.name ?? language.nameFr,
        }))}
        selectedItems={formData.languages || []}
        onSelectionChange={(items) => {
          handleFormChange('languages', items)
        }}
        isFetching={isFetchingLanguages}
        fetchNextPage={() => {
          fetchNextPageLanguages()
        }}
        hasNextPage={hasNextPageLanguages}
        isFetchingNextPage={isFetchingNextPageLanguages}
        onSearch={(text) => setSearchText(text)}
      />

      <GenericSelectionModal
        isVisible={
          isOriginModalVisible ||
          isAddressModalVisible ||
          isLivedPlacesModalVisible ||
          isNationalitiesModalVisible
        }
        setIsVisible={(val) => {
          if (isOriginModalVisible) {
            setIsOriginModalVisible(val)
          } else if (isAddressModalVisible) {
            setIsAddressModalVisible(val)
          } else if (isLivedPlacesModalVisible) {
            setIsLivedPlacesModalVisible(val)
          } else if (isNationalitiesModalVisible) {
            setIsNationalitiesModalVisible(val)
          }
        }}
        title={
          isOriginModalVisible
            ? t('form.origins')
            : isLivedPlacesModalVisible
              ? t('form.livedPlaces')
              : isNationalitiesModalVisible
                ? t('form.nationalities')
                : t('form.livingPlace')
        }
        placeholder={t('form.searchPlace')}
        availableItems={
          isAddressModalVisible || isLivedPlacesModalVisible
            ? cities.map(cityToSelectionItem)
            : countries.map((country) => ({
                id: country.code,
                name: country.name ?? country.nameFr,
              }))
        }
        selectedItems={
          isNationalitiesModalVisible
            ? formData.nationalities || []
            : isLivedPlacesModalVisible
              ? formData.residenceHistory || []
              : isOriginModalVisible
                ? ((formData.origins || []) as SelectionItem[])
                : [formData.residence as SelectionItem].filter(Boolean)
        }
        onSelectionChange={(items) => {
          // An empty selection is kept: the last city or country can be removed.
          if (isOriginModalVisible) {
            handleFormChange('origins', items)
          } else if (isAddressModalVisible) {
            handleFormChange('residence', items[0])
          } else if (isLivedPlacesModalVisible) {
            handleFormChange('residenceHistory', items)
          } else if (isNationalitiesModalVisible) {
            handleFormChange('nationalities', items)
          }
        }}
        multiSelect={
          isLivedPlacesModalVisible || isNationalitiesModalVisible || isOriginModalVisible
        }
        isFetching={isFetchingCities || isFetchingCountries}
        fetchNextPage={() => {
          if (isAddressModalVisible || isLivedPlacesModalVisible) {
            fetchNextPageCities()
          } else if (isOriginModalVisible || isNationalitiesModalVisible) {
            fetchNextPageCountries()
          }
        }}
        hasNextPage={
          isAddressModalVisible || isLivedPlacesModalVisible
            ? hasNextPageCities
            : isOriginModalVisible || isNationalitiesModalVisible
              ? hasNextPageCountries
              : false
        }
        isFetchingNextPage={isFetchingNextPageCities || isFetchingNextPageCountries}
        onSearch={(text) => setSearchText(text)}
      />

      <GenericSelectionModal
        isVisible={isHashtagsModalVisible}
        setIsVisible={setIsHashtagsModalVisible}
        title={t('form.hashtags')}
        placeholder={t('form.searchHashtags')}
        availableItems={hashtags.map((hashtag) => ({
          id: hashtag.id,
          name: hashtag.name,
        }))}
        selectedItems={formData.pinsHashtags || []}
        onSelectionChange={(items) => {
          handleFormChange('pinsHashtags', items)
        }}
        isHashtagsModal
        isFetching={isFetchingHashtags}
        fetchNextPage={() => {
          fetchNextPageHashtags()
        }}
        hasNextPage={hasNextPageHashtags}
        isFetchingNextPage={isFetchingNextPageHashtags}
        onSearch={(text) => setSearchText(text)}
      />
      <GenericSelectionModal
        isVisible={isCategoriesModalVisible}
        setIsVisible={setIsCategoriesModalVisible}
        title={t('profileEdit.categories')}
        placeholder={t('profileEdit.searchCategories')}
        availableItems={categories.map((category) => ({
          id: category.id,
          name: category.name,
          photo: category.avatarUrl || '',
        }))}
        selectedItems={formData.pinsCategories || []}
        onSelectionChange={(items) => {
          handleFormChange('pinsCategories', items)
        }}
        isCategoriesModal
        isFetching={isFetchingCategories}
        fetchNextPage={() => {
          fetchNextPageCategories()
        }}
        hasNextPage={hasNextPageCategories}
        isFetchingNextPage={isFetchingNextPageCategories}
        onSearch={(text) => setSearchText(text)}
      />

      <GenericSelectionModal
        isVisible={isProfessionsModalVisible}
        setIsVisible={setIsProfessionsModalVisible}
        title={t('importContacts.profession')}
        placeholder={t('form.searchProfession')}
        availableItems={groupedProfessions}
        selectedItems={formData.professions || []}
        multiSelect={true}
        isFetching={isFetchingProfessions}
        isFetchingNextPage={isFetchingNextPageProfessions}
        hasNextPage={hasNextPageProfessions}
        fetchNextPage={fetchNextPageProfessions}
        onSearch={(search) => {
          setSearchText(search)
        }}
        onSelectionChange={(items) => {
          handleFormChange('professions', items)
        }}
      />

      <AvailabilityModal
        isVisible={isDisponibilityModalVisible}
        setIsVisible={setIsDisponibilityModalVisible}
        disponibilities={formData.disponibilities || []}
        setDisponibilities={(disponibilities) =>
          handleFormChange('disponibilities', disponibilities)
        }
        bestMoments={formData.bestMoments || []}
        setBestMoments={(bestMoments) => handleFormChange('bestMoments', bestMoments)}
      />
    </Screen>
  )
}
